/**
 * Open Road cart drawer — Cart D "Frequently bought together".
 *
 * Dawn ships no built-in drawer upsell, so this fetches one real
 * suggestion from Shopify's /recommendations/products.json endpoint (the
 * same endpoint sections/open-road-product-docket.liquid already uses via
 * routes.product_recommendations_url for the PDP "related parts" row) and
 * renders it into the #CartDrawer-Upsell placeholder that snippets/
 * cart-drawer.liquid leaves hidden and empty.
 *
 * Never shows an empty box: the placeholder stays [hidden] unless a real,
 * available, not-already-in-cart product is found.
 *
 * Re-population: cart-drawer.js replaces the *entire* .drawer__inner (via
 * CartDrawer.renderContents on add-to-cart, and CartItems.updateQuantity on
 * qty +/-), which wipes this placeholder back to its empty/hidden liquid
 * state. Rather than hook every possible update path, a MutationObserver on
 * the persistent <cart-drawer> element notices whenever the placeholder
 * reappears unpopulated and re-fetches.
 */
(function () {
  var UPSELL_ID = 'CartDrawer-Upsell';

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(cents / 100);
    } catch (e) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  function clearUpsell(container) {
    container.hidden = true;
    container.innerHTML = '';
    container.removeAttribute('data-or-upsell-ready');
  }

  function renderUpsell(container, product, variant, currency) {
    container.innerHTML = '';

    var thumb = document.createElement('div');
    thumb.className = 'or-cart-upsell__thumb';
    if (product.featured_image) {
      thumb.style.backgroundImage = 'url(' + JSON.stringify(product.featured_image) + ')';
    }
    thumb.setAttribute('role', 'img');
    thumb.setAttribute('aria-label', product.title);

    var meta = document.createElement('div');
    meta.className = 'or-cart-upsell__meta';
    meta.innerHTML =
      '<span class="or-cart-upsell__eyebrow">&#9733; Goes well with</span> ' +
      '<b class="or-cart-upsell__title">' + escapeHtml(product.title) + '</b> &middot; ' +
      escapeHtml(formatMoney(variant.price, currency));

    var addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'or-cart-upsell__add';
    addButton.setAttribute('data-variant-id', variant.id);
    addButton.setAttribute('aria-label', 'Add ' + product.title + ' to cart');
    addButton.textContent = '+';

    container.appendChild(thumb);
    container.appendChild(meta);
    container.appendChild(addButton);
    container.hidden = false;
    container.setAttribute('data-or-upsell-ready', 'true');
  }

  function populate(container) {
    if (!container || container.getAttribute('data-or-upsell-busy') === 'true') return;
    container.setAttribute('data-or-upsell-busy', 'true');

    fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          clearUpsell(container);
          return;
        }
        var inCartProductIds = cart.items.map(function (item) { return item.product_id; });
        var seedProductId = cart.items[0].product_id;
        var currency = cart.currency;

        return fetch('/recommendations/products.json?product_id=' + seedProductId + '&limit=10&intent=related')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            var products = (data && data.products) || [];
            var pick = null;
            for (var i = 0; i < products.length; i++) {
              var candidate = products[i];
              if (candidate.available && inCartProductIds.indexOf(candidate.id) === -1) {
                pick = candidate;
                break;
              }
            }
            if (!pick || !pick.variants || !pick.variants.length) {
              clearUpsell(container);
              return;
            }
            renderUpsell(container, pick, pick.variants[0], currency);
          });
      })
      .catch(function () {
        clearUpsell(container);
      })
      .finally(function () {
        container.removeAttribute('data-or-upsell-busy');
      });
  }

  function ensurePopulated() {
    var container = document.getElementById(UPSELL_ID);
    if (
      container &&
      !container.hasAttribute('data-or-upsell-ready') &&
      container.getAttribute('data-or-upsell-busy') !== 'true'
    ) {
      populate(container);
    }
  }

  function handleAddClick(evt) {
    var button = evt.target.closest && evt.target.closest('.or-cart-upsell__add');
    if (!button || button.disabled) return;
    evt.preventDefault();

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    var cartDrawer = document.querySelector('cart-drawer');
    var formData = new FormData();
    formData.append('id', button.getAttribute('data-variant-id'));
    formData.append('quantity', '1');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      cartDrawer.getSectionsToRender().forEach(function (section) {
        formData.append('sections', section.id);
      });
      formData.append('sections_url', window.location.pathname);
    }

    var config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(window.routes.cart_add_url, config)
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || 'Could not add to cart');
        }
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(response);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'or-cart-upsell', cartData: response });
        }
      })
      .catch(function () {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', handleAddClick);

  function init() {
    ensurePopulated();
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      var observer = new MutationObserver(function () {
        ensurePopulated();
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
